import styled from "styled-components";
import {
  siteGray,
  siteDarkBlue,
  sitePurple,
  siteYellow
} from "../../providers/Theme";
import {
  bodyFontBase,
  bodyFontMd,
  buttonFontSize
} from "../shared.styled.components";
import HeroPhoto from "../../assets/donate-hero.jpg";
const LogoMark = "/logo192.png";

// ── Hero ─────────────────────────────────────────────────────────────────────

export const DonateHero = styled.section`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;

  ${({ theme }) => theme.lg`
    flex-direction: row;
    min-height: 420px;
  `}
`;

export const HeroLeft = styled.div`
  background: ${siteDarkBlue};
  padding: 3rem 2rem;
  display: flex;
  flex-direction: column;
  justify-content: center;

  ${({ theme }) => theme.lg`
    width: 48%;
    flex-shrink: 0;
    padding: 4rem 3.5rem;
  `}
`;

export const HeroHeadline = styled.h1`
  color: white;
  font-weight: 900;
  font-size: 2.5rem;
  line-height: 1.05;
  text-transform: uppercase;
  margin-bottom: 1.25rem;

  ${({ theme }) => theme.md`
    font-size: 3rem;
  `}

  ${({ theme }) => theme.lg`
    font-size: 3.5rem;
  `}
`;

export const HeroBody = styled.p`
  color: white;
  font-size: ${bodyFontBase};
  line-height: 1.6;
  margin-bottom: 2rem;
  max-width: 36rem;

  ${({ theme }) => theme.md`
    font-size: ${bodyFontMd};
  `}
`;

export const ProtectButton = styled.button`
  display: inline-block;
  background: linear-gradient(to right, #1a4fdb, #3bb8f8);
  color: white;
  font-weight: 800;
  font-size: ${buttonFontSize};
  letter-spacing: 0.06em;
  text-transform: uppercase;
  border: none;
  border-radius: 0.6rem;
  padding: 0.9rem 2rem;
  cursor: pointer;
  align-self: flex-start;
  transition: filter 0.15s ease;
  min-width: 280px;

  &:hover {
    filter: brightness(0.92);
  }
`;

export const MonthlyLink = styled.p`
  color: rgba(255, 255, 255, 0.75);
  font-size: 0.8rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  font-weight: 600;
  margin: 0.75rem 0 0;
  cursor: pointer;

  &:hover {
    color: white;
    text-decoration: underline;
  }
`;

export const HeroLogoMark = styled.img.attrs({ src: LogoMark, alt: "" })`
  position: absolute;
  width: 150px;
  top: 1.5rem;
  right: calc(52% - 80px);
  z-index: 3;
  pointer-events: none;
  display: none;

  ${({ theme }) => theme.lg`
    display: block;
  `}
`;

export const HeroLogoStem = styled.div`
  position: absolute;
  width: 6px;
  top: calc(1.5rem + 132px);
  bottom: 0;
  right: 52%;
  z-index: 2;
  pointer-events: none;
  display: none;
  background: linear-gradient(
    to bottom,
    #0b4ad1,
    #8b5cf6,
    #ec4899,
    rgba(236, 72, 153, 0)
  );
  border-radius: 0 0 3px 3px;

  ${({ theme }) => theme.lg`
    display: block;
  `}
`;

// ── Why Monthly Giving Matters ────────────────────────────────────────────────

export const WhyMonthlySection = styled.section`
  background: white;
  padding: 2.5rem 2rem;

  ${({ theme }) => theme.lg`
    padding: 3rem 4rem;
  `}
`;

export const WhyMonthlyInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;

  ${({ theme }) => theme.lg`
    flex-direction: row;
    align-items: center;
    gap: 3rem;
  `}
`;

export const WhyMonthlyLeft = styled.div`
  flex: 1;
`;

export const WhyMonthlyHeading = styled.h2`
  color: ${siteDarkBlue};
  font-weight: 800;
  font-size: 1.6rem;
  margin-bottom: 1.25rem;

  ${({ theme }) => theme.md`
    font-size: 1.9rem;
  `}
`;

export const BulletList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
`;

export const BulletItem = styled.li`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  font-size: ${bodyFontBase};
  color: #333;
  font-weight: 500;
`;

export const MonthlyCard = styled.div`
  background: #eef3fc;
  border: 1.5px solid #8faad8;
  border-radius: 0.75rem;
  padding: 1.75rem 2rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-width: 480px;
  align-self: center;

  ${({ theme }) => theme.lg`
    flex-shrink: 0;
    width: 400px;
    max-width: none;
    align-self: auto;
  `}
`;

export const MonthlyCardLabel = styled.p`
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${siteDarkBlue};
  margin: 0;

  span {
    color: #1a8fd1;
  }
`;

export const MonthlyCardButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background: ${siteDarkBlue};
  color: white;
  border: none;
  border-radius: 9999px;
  padding: 0.65rem 1.25rem;
  font-size: 1.3rem;
  font-weight: 700;
  cursor: pointer;
  width: 100%;
  transition: filter 0.15s ease;

  &:hover {
    filter: brightness(0.95);
  }
`;

export const SectionDivider = styled.hr`
  border: none;
  border-top: 1px solid #dee2e6;
  margin: 0;
`;

// ── Donate Now CTA ────────────────────────────────────────────────────────────

export const DonateNowSection = styled.section`
  background: white;
  padding: 2rem 2rem 2.5rem;
  display: flex;
  justify-content: center;

  ${({ theme }) => theme.lg`
    padding: 2rem 4rem 3rem;
  `}
`;

export const DonateNowButton = styled.button`
  background: linear-gradient(to right, #1a4fdb, #22c55e);
  color: white;
  font-weight: 900;
  font-size: 1.8rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  border: none;
  border-radius: 0.6rem;
  padding: 1.1rem 3rem;
  cursor: pointer;
  width: 100%;
  max-width: 520px;
  transition: filter 0.15s ease;

  &:hover {
    filter: brightness(0.92);
  }
`;

// ── What Your Gift Makes Possible ────────────────────────────────────────────

export const WhatYourGiftSection = styled.section`
  background: white;
  padding: 2.5rem 2rem;

  ${({ theme }) => theme.lg`
    padding: 3rem 4rem;
  `}
`;

export const GiftSectionHeading = styled.h2`
  color: ${siteDarkBlue};
  font-weight: 800;
  font-size: 1.6rem;
  margin-bottom: 1.5rem;

  ${({ theme }) => theme.md`
    font-size: 1.9rem;
  `}
`;

export const GiftGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  ${({ theme }) => theme.lg`
    grid-template-columns: repeat(4, 1fr);
    gap: 1.5rem;
  `}
`;

export const GiftCard = styled.button`
  background: transparent;
  border: none;
  border-radius: 0.75rem;
  overflow: hidden;
  cursor: pointer;
  text-align: left;
  padding: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  transition:
    transform 0.15s ease,
    box-shadow 0.15s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
  }
`;

export const GiftCardHeader = styled.div<{ $color: string }>`
  background: ${({ $color }) => $color};
  color: white;
  font-size: 1.9rem;
  font-weight: 900;
  text-align: center;
  padding: 0.75rem 1rem;
  width: 100%;
`;

export const GiftCardBody = styled.div<{ $bg: string }>`
  background: ${({ $bg }) => $bg};
  padding: 1rem;
  flex: 1;
  font-size: 1rem;
  color: #444;
  line-height: 1.5;
  text-align: left;
`;

export const GiftCardHighlight = styled.span<{ $color: string }>`
  color: ${({ $color }) => $color};
  font-weight: 700;
`;

// ── Giving Levels ─────────────────────────────────────────────────────────────

export const GivingLevelsSection = styled.section`
  background: white;
  padding: 2.5rem 2rem;
  display: flex;
  justify-content: center;

  ${({ theme }) => theme.lg`
    padding: 3rem 4rem;
  `}
`;

export const GivingLevelsCard = styled.div`
  width: 100%;
  max-width: 420px;
  border-radius: 0.75rem;
  overflow: hidden;
`;

export const GivingLevelsHeader = styled.div`
  background: ${siteDarkBlue};
  color: white;
  font-size: 1.2rem;
  font-weight: 700;
  text-align: center;
  padding: 0.85rem 1.5rem;
`;

export const GivingLevelsBody = styled.div`
  background: #eef1fa;
  padding: 1.25rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
`;

export const GivingLevelRow = styled.button`
  display: flex;
  align-items: center;
  gap: 1rem;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  text-align: left;

  &:hover span:first-child {
    filter: brightness(0.9);
  }
`;

export const GivingLevelBadge = styled.span<{ $color: string }>`
  background: ${({ $color }) => $color};
  color: white;
  font-weight: 800;
  font-size: 1rem;
  border-radius: 0.4rem;
  padding: 0.3rem 0.85rem;
  min-width: 72px;
  text-align: center;
  flex-shrink: 0;
`;

export const GivingLevelName = styled.span<{ $color: string }>`
  color: ${({ $color }) => $color};
  font-weight: 700;
  font-size: 1.1rem;
`;

// ── Make Your Impact CTA ──────────────────────────────────────────────────────

export const MakeImpactSection = styled.section`
  background: white;
  padding: 2rem 2rem 3rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;

  ${({ theme }) => theme.lg`
    flex-direction: row;
    padding: 2rem 4rem 3.5rem;
  `}
`;

export const ImpactDividerLine = styled.div`
  display: none;

  ${({ theme }) => theme.lg`
    display: block;
    flex: 1;
    height: 1px;
    background: #dee2e6;
  `}
`;

export const MakeImpactButton = styled.button`
  background: linear-gradient(to right, ${sitePurple}, ${siteYellow});
  color: white;
  font-weight: 900;
  font-size: 1.3rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  border: none;
  border-radius: 0.6rem;
  padding: 1rem 2rem;
  cursor: pointer;
  width: 100%;
  max-width: 420px;
  transition: filter 0.15s ease;

  ${({ theme }) => theme.lg`
    font-size: 1.6rem;
    padding: 1rem 2.5rem;
    width: auto;
    max-width: none;
  `}

  &:hover {
    filter: brightness(0.92);
  }
`;

// ── Other Ways to Give ───────────────────────────────────────────────────────

export const OtherWaysSection = styled.section`
  background: white;
  padding: 2.5rem 2rem;

  ${({ theme }) => theme.lg`
    padding: 3rem 4rem;
  `}
`;

export const OtherWaysHeading = styled.h2`
  color: ${siteDarkBlue};
  font-weight: 800;
  font-size: 1.6rem;
  margin-bottom: 0.75rem;

  ${({ theme }) => theme.md`
    font-size: 1.9rem;
  `}
`;

export const OtherWaysInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;

  ${({ theme }) => theme.md`
    flex-direction: row;
    align-items: center;
    gap: 2rem;
  `}
`;

export const MailColumn = styled.div`
  flex: 1;

  ${({ theme }) => theme.md`
    padding-left: 3rem;
  `}
`;

export const MailLabel = styled.p`
  color: ${siteDarkBlue};
  font-size: ${bodyFontBase};
  margin-bottom: 0.35rem;
`;

export const MailOrgName = styled.p`
  color: ${siteDarkBlue};
  font-weight: 800;
  font-size: ${bodyFontMd};
  margin-bottom: 0.25rem;
`;

export const MailAddress = styled.p`
  color: #444;
  font-size: ${bodyFontBase};
  line-height: 1.6;
  margin: 0;
`;

export const DonateCalloutColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.25rem;
  text-align: center;
`;

export const DonateCalloutText = styled.p`
  color: #444;
  font-size: ${bodyFontBase};
  line-height: 1.6;
  margin: 0;

  strong {
    color: ${siteDarkBlue};
  }
`;

// ── Trust Bar ─────────────────────────────────────────────────────────────────

export const TrustBar = styled.div`
  background: ${siteGray};
  padding: 0.85rem 2rem;
  display: flex;
  align-items: center;
  gap: 2.5rem;
  flex-wrap: wrap;
  justify-content: flex-start;

  ${({ theme }) => theme.lg`
    padding: 0.85rem 3.5rem;
  `}
`;

export const TrustItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 1rem;
  font-weight: 600;
  color: #333;
`;

export const HeroImage = styled.div`
  background-image: url(${HeroPhoto});
  background-size: cover;
  background-position: center top;
  height: 280px;

  ${({ theme }) => theme.md`
    height: 380px;
  `}

  ${({ theme }) => theme.lg`
    flex: 1;
    height: auto;
  `}
`;
